import classNames from 'classnames';
import React, { ChangeEvent, FC, useState } from 'react';

import { TSelect } from '@/src/assets/helpers/getFilteredData';
import { getFilteredData } from '@/src/assets/helpers/getFilteredData';
import { ITheme } from '@/src/assets/interfaces/themeInterface';
import { baseUrl } from '@/src/assets/utils/baseUrl';

import { Title } from '../../UI/title/Title';
import Wrapper from '../../UI/wrapper/Wrapper';
import Breadcrumbs from '../../breadcrumbs/Breadcrumbs';
import Layout from '../../layout/Layout';
import ThemesList from '../../themesList/ThemesList';

import styles from './ThemeArchive.module.scss';

type TThemesData = {
    themesData: Partial<ITheme>[];
};

const ThemeArchive: FC<TThemesData> = ({ themesData }) => {
    const [activeTag, setActiveTag] = useState('All Categories');

    const [activeOption, setActiveOption] = useState<TSelect>('popularity');

    const handleOptionChange = (e: ChangeEvent<HTMLSelectElement>) => {
        setActiveOption(e.target.value as TSelect);
    };

    const filteredThemesData = getFilteredData(themesData, activeOption);

    const filtersArr = themesData
        .map((item) => item.demoList)
        .flat()
        .map((item) => item?.demoTag);
    const filters = [...new Set(filtersArr)];

    const handleFilters = (filter: string) => {
        setActiveTag(filter);
    };

    const jsonLd = {
        '@context': 'https://schema.org/',
        '@type': 'ItemList',
        name: 'Themes Archive',
        description: `Look through our extensive collection of Ghost themes to find the perfect match for your website's style and needs.`,
        itemListElement: themesData
            ? themesData.map((item, i) => {
                  return {
                      '@type': 'ListItem',
                      position: `${i + 1}`,
                      url: `${baseUrl(`themes/${item.slug}`)}`,
                  };
              })
            : null,
    };

    const heading = 'Explore Our Themes';
    const excerpt = `Look through our extensive collection of Ghost themes to find the perfect match for your website's style and needs.`;

    return (
        <Layout
            meta={{
                metaTitle: 'Ghost Themes & Templates | High Five Themes',
                metaDescription:
                    'Browse our collection of easy-to-use and professionally crafted Ghost themes. Find the perfect Ghost theme for your personal blog, magazine, and news website.',
                ogTitle: 'Ghost Themes & Templates | High Five Themes',
                ogDescription:
                    'Browse our collection of easy-to-use and professionally crafted Ghost themes. Find the perfect Ghost theme for your personal blog, magazine, and news website.',
            }}
            jsonLD={jsonLd}>
            <Wrapper>
                <Breadcrumbs />
                <div className={styles.info}>
                    <Title text={heading} size="xl" className={styles['section-heading']} />
                    <p className={`excerpt excerpt-lg excerpt-wrap ${styles.excerpt}`}>{excerpt}</p>
                    <div className={styles.filter__bars}>
                        <ul className={styles.filters}>
                            <li
                                key={`All Categories`}
                                className={`${styles.filter} ${
                                    activeTag === 'All Categories' && styles['active']
                                }`}
                                onClick={() => handleFilters('All Categories')}>
                                All Categories
                            </li>
                            {filters.map((filter, i) => (
                                <li
                                    key={`${filter}-${i + 1}`}
                                    className={`${styles.filter} ${
                                        activeTag === filter && styles['active']
                                    }`}
                                    onClick={() => handleFilters(filter!)}>
                                    {filter}
                                </li>
                            ))}
                        </ul>
                        <div className={styles.select__wrapper}>
                            <span>Order by:</span>
                            <select
                                id="select-filter"
                                className={classNames(styles.input, styles.select)}
                                aria-label="Filter"
                                value={activeOption}
                                onChange={handleOptionChange}>
                                <option value="popularity">Popularity</option>
                                <option value="latest">Latest first</option>
                                <option value="name">Name</option>
                            </select>
                        </div>
                    </div>
                </div>
                <div className={styles[`themes-wrapper`]}>
                    <ThemesList
                        themesData={filteredThemesData!}
                        activeTag={activeTag}
                        handleFilters={handleFilters}
                    />
                </div>
            </Wrapper>
        </Layout>
    );
};

export default ThemeArchive;
